// src/models/user.model.ts
// zod/mini — 클래식 zod 대비 메인 번들 gzip ~57KB 절감(체이닝 대신 함수형 API).
import * as z from "zod/mini";

// 서버가 빠뜨리거나 null 로 보낸 필드를 기본값으로(클래식 optional+nullable+transform 과 동일).
export const orDefault = <T extends z.ZodMiniType, O>(
  schema: T,
  fallback: (v: z.output<T> | null | undefined) => O,
) => z.pipe(z.nullish(schema), z.transform(fallback));

export const HALAL_STRICTNESS = ["strict", "moderate", "lenient"] as const;

export type HalalStrictness = (typeof HALAL_STRICTNESS)[number];

export const USER_TYPES = [
  "USER", "GUEST", "PROVIDER", "PLACE_PROVIDER", "INTERNAL_USER", "ADMIN",
] as const;

export type UserType = (typeof USER_TYPES)[number];

export const UserDataSchema = z.object({
  uuid: z.string(),

  email: orDefault(z.string(), (v) => v ?? ""),
  full_name: orDefault(z.string(), (v) => v ?? ""),
  user_name: orDefault(z.string(), (v) => v ?? ""),

  country_code: orDefault(z.string(), (v) => v ?? ""),
  contact: orDefault(z.string(), (v) => v ?? ""),

  currency: orDefault(z.string(), (v) => v ?? ""),
  // 설정 2종. `type`/`roles` 와 같은 이유로 z.enum 을 쓰지 않는다 — 서버가
  // 모르는 값을 하나 추가하는 순간 파싱이 예외를 던져 로그인이 통째로 죽는다.
  // 모르는 값은 안전한 기본값으로 떨어뜨린다.
  halal_strictness: orDefault(z.string(), (v) =>
    (HALAL_STRICTNESS as readonly string[]).includes(v ?? "") ? (v as HalalStrictness) : "moderate",
  ),
  // 서버가 미설정 시 country_code 매핑 → 'en' 으로 채워 내려준다.
  translation_language: orDefault(z.string(), (v) => v ?? ""),
  exchange_rate: orDefault(z.string(), (v) => v ?? ""),

  // 서버가 아는 권한이 늘어도 로그인이 죽지 않도록 관대하게 받는다.
  //
  // 종전에는 z.enum 이라 목록에 없는 값이 오면 **파싱이 예외를 던져 로그인이 통째로
  // 실패**했다. PLACE_PROVIDER 가 목록에 빠져 있어 배달 파트너 계정은 지금도 이 문제로
  // 로그인이 안 된다. 알 수 없는 값은 최소 권한인 "USER" 로 떨어뜨린다.
  type: orDefault(z.string(), (v) =>
    (USER_TYPES as readonly string[]).includes(v ?? "") ? (v as UserType) : "USER",
  ),

  // 권한의 진실은 이 배열이다 — `type` 은 스칼라라 겸직(PROVIDER 이면서
  // PLACE_PROVIDER)을 표현할 수 없어 레거시로 남는다. 백필이 아직 닿지 않은
  // 사용자는 빈 배열이므로, 비어 있으면 `type` 으로 폴백해야 한다.
  roles: orDefault(z.array(z.string()), (v) => v ?? []),

  login_type: orDefault(z.enum(["GUEST", "EMAIL", "GOOGLE", "APPLE", "KAKAO"]), (v) => v ?? "EMAIL"),

  terms_of_service: orDefault(z.boolean(), (v) => v ?? false),
  marketing_email: orDefault(z.boolean(), (v) => v ?? false),
  marketing_app: orDefault(z.boolean(), (v) => v ?? false),
  // 야간(21–08시) 광고성 앱 푸시 동의 — 정보통신망법 제50조 ③. 앱 푸시 동의에 딸림.
  marketing_night: orDefault(z.boolean(), (v) => v ?? false),

  image: orDefault(z.string(), (v) => v ?? null),

  created_at: orDefault(z.string(), (v) => v ?? ""),
  updated_at: orDefault(z.string(), (v) => v ?? ""),

});

export type UserData = z.infer<typeof UserDataSchema>;
